import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdPosition } from '../../types/Ad.api.type';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';

const POSITION_TABS: { key: AdPosition; label: string }[] = [
  { key: 'MAIN_FEED_TOP', label: '메인 상단' },
  { key: 'MAIN_FEED', label: '메인 피드' },
  { key: 'POPULAR_FEED_TOP', label: '인기설문 상단' },
  { key: 'POPULAR_FEED', label: '인기설문' },
  { key: 'MY_FEED_TOP', label: '내설문 상단' },
  { key: 'MY_FEED', label: '내설문' },
  { key: 'DETAIL_COMMENT', label: '상세 댓글' },
];

const AdRotationStatus = () => {
  const [selectedPosition, setSelectedPosition] = useState<AdPosition>('MAIN_FEED');
  const [weights, setWeights] = useState<Record<number, number>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  const { data: rotationItems, isLoading } = useQuery({
    queryKey: ['adRotation', selectedPosition],
    queryFn: () => adApi.getRotation(selectedPosition).then(res => res.data),
  });

  useEffect(() => {
    if (rotationItems) {
      const initial: Record<number, number> = {};
      rotationItems.forEach(item => { initial[item.positionId] = item.weight; });
      setWeights(initial);
      setHasChanges(false);
    }
  }, [rotationItems]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const items = Object.entries(weights).map(([positionId, weight]) => ({
        positionId: Number(positionId),
        weight,
      }));
      return adApi.updateWeights({ items });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adRotation', selectedPosition] });
      toast.success('가중치가 저장되었습니다.');
      setHasChanges(false);
    },
    onError: () => {
      toast.error('저장에 실패했습니다.');
    },
  });

  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);

  const handleWeightChange = (positionId: number, value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1) return;
    setWeights(prev => ({ ...prev, [positionId]: num }));
    setHasChanges(true);
  };

  const handleTabChange = (pos: AdPosition) => {
    if (hasChanges && !window.confirm('저장하지 않은 변경사항이 있습니다. 이동하시겠습니까?')) return;
    setSelectedPosition(pos);
  };

  return (
    <div className="space-y-4">
      {/* 위치별 탭 */}
      <div className="flex flex-wrap gap-2">
        {POSITION_TABS.map(tab => (
          <Button
            key={tab.key}
            variant={selectedPosition === tab.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* 로테이션 목록 */}
      {isLoading ? (
        <div className="py-8 text-center text-gray-500">로딩 중...</div>
      ) : !rotationItems || rotationItems.length === 0 ? (
        <Card>
          <div className="py-12 text-center text-gray-400">
            이 위치에 활성 광고가 없습니다
          </div>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {rotationItems.map(item => {
              const currentWeight = weights[item.positionId] ?? item.weight;
              const percent = totalWeight > 0 ? (currentWeight / totalWeight * 100) : 0;

              return (
                <Card key={item.positionId}>
                  <div className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* 광고 정보 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={item.adType === 'SURVEY' ? 'border-blue-300 text-blue-600' : 'border-orange-300 text-orange-600'}>
                            {item.adType === 'SURVEY' ? '설문' : '외부'}
                          </Badge>
                          <span className="font-medium text-sm text-gray-900 truncate">
                            {item.adTitle}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400">ID: {item.adId}</span>
                      </div>

                      {/* 가중치 입력 */}
                      <div className="flex items-center gap-3 sm:w-[260px]">
                        <div className="flex items-center gap-1.5">
                          <label className="text-xs text-gray-500 whitespace-nowrap">가중치</label>
                          <input
                            type="number"
                            min={1}
                            value={currentWeight}
                            onChange={e => handleWeightChange(item.positionId, e.target.value)}
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                          />
                        </div>

                        {/* 퍼센트 바 */}
                        <div className="flex-1 min-w-[80px]">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{percent.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(percent, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* 저장 버튼 */}
          <div className="flex justify-end">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!hasChanges || saveMutation.isPending}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saveMutation.isPending ? '저장 중...' : '저장'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdRotationStatus;
